import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { AnimatePresence } from "framer-motion";

import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";
import DropIcon from "../DropIcon/DropIcon";
import { choosePlan } from "../../store/slices/subscription/asyncThunks";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { selectUser } from "../../store/slices/user/slice";
import { priceFormatter, showAsianPayments } from "../../utils/utils";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";

import telegramStars1 from "../../assets/images/telegram-payment-guide/telegram_stars_1.webp";
import telegramStars2 from "../../assets/images/telegram-payment-guide/telegram_stars_2.webp";
import telegramStars3 from "../../assets/images/telegram-payment-guide/telegram_stars_3.webp";

import {
  ActionArea,
  ArrowButton,
  ArrowButtonLabel,
  BackButton,
  Backdrop,
  Body,
  BuyAmount,
  BuyButton,
  Card,
  CardFooterInfo,
  CardFooterLine,
  CardHero,
  CardHeroBonus,
  CardHeroIcon,
  CardHeroSubtitle,
  CardHeroText,
  CardHeroValue,
  CardHeroValueLabel,
  CardHeroValueNumber,
  CarouselSlide,
  CarouselSpacer,
  CarouselTrack,
  CarouselViewport,
  CarouselWrapper,
  CloseButton,
  CryptoLink,
  Dot,
  DotsRow,
  DragHandle,
  Feature,
  FeatureIcon,
  FeatureLabel,
  FeaturesList,
  Footnote,
  Header,
  LockBadge,
  MethodBadge,
  MethodIcons,
  MethodLabel,
  PaymentBox,
  PaymentGrid,
  PaymentMethodCard,
  PayNowButton,
  PlanCard,
  PlanCardGlow,
  PlanSummary,
  PlanSummaryDrops,
  PlanSummaryHeader,
  PlanSummaryIcon,
  PlanSummaryName,
  PlanSummaryPrice,
  PlanSummaryText,
  PopularBadge,
  SecureNote,
  TelegramGuide,
  TelegramGuideFooter,
  TelegramGuideHeader,
  TelegramGuidePreviews,
  TelegramGuideThumb,
  Title,
} from "./TopUpModal.styles";

const MOBILE_SHEET_TRANSITION_MS = 320;
const SWIPE_CLOSE_DISTANCE = 86;
const SWIPE_CLOSE_VELOCITY = 0.65;
const BUY_BUTTON_DROP_GRADIENT_ID = "top-up-buy-button-drop-gradient";

/* Inline payment-method icons. Project's shipped SVGs hard-code white
   strokes (invisible in light theme), so icons live inline with
   currentColor and per-icon brand overrides. */

const BTCIcon = (props) => (
  <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      fill="currentColor"
      d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zm-141.651-35.33c4.937-32.999-20.191-50.739-54.55-62.573l11.146-44.702-27.213-6.781-10.851 43.524c-7.154-1.783-14.502-3.464-21.803-5.13l10.929-43.81-27.198-6.781-11.153 44.686c-5.922-1.349-11.735-2.682-17.377-4.084l.031-.14-37.53-9.37-7.239 29.062s20.191 4.627 19.765 4.913c11.022 2.751 13.014 10.044 12.68 15.825l-12.696 50.925c.76.194 1.744.473 2.829.907-.907-.225-1.876-.473-2.876-.713l-17.796 71.338c-1.349 3.348-4.767 8.37-12.471 6.464.271.395-19.78-4.937-19.78-4.937l-13.51 31.147 35.414 8.827c6.588 1.651 13.045 3.379 19.4 5.006l-11.262 45.213 27.182 6.781 11.153-44.733a1038.209 1038.209 0 0 0 21.687 5.627l-11.115 44.523 27.213 6.781 11.262-45.128c46.404 8.781 81.299 5.239 95.986-36.727 11.836-33.79-.589-53.281-25.004-65.991 17.78-4.098 31.174-15.792 34.747-39.949zm-62.177 87.179c-8.41 33.79-65.308 15.523-83.755 10.943l14.944-59.899c18.446 4.603 77.6 13.717 68.811 48.956zm8.417-87.667c-7.673 30.736-55.031 15.12-70.393 11.292l13.548-54.327c15.363 3.828 64.836 10.973 56.845 43.035z"
    />
  </svg>
);

const USDTIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" {...props} viewBox="0 0 24 24">
    <path
      fill="#009393"
      d="m12 20.143l-8.57-8.456L6.7 6.429h10.603l3.27 5.258zm.857-7.714v-1.046c1.543.077 2.983.377 3.429.767c-.519.454-2.374.784-4.286.784s-3.767-.33-4.286-.784c.442-.39 1.886-.686 3.429-.771v1.05zm-5.143-.283v.63c.442.39 1.882.686 3.429.771v2.31h1.714v-2.314c1.543-.077 2.987-.377 3.429-.767V11.52c-.442-.39-1.886-.69-3.429-.771v-.892h2.572V8.572H8.572v1.285h2.571v.892c-1.547.081-2。987.381-3。429。771z"
    />
  </svg>
);

const ETHIcon = (props) => (
  <svg viewBox="0 0 320 512" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      fill="currentColor"
      d="M311.9 260.8L160 353.6 8 260.8 160 0l151.9 260.8zM160 383.4L8 290.6 160 512l152-221.4-152 92.8z"
    />
  </svg>
);

const CreditCardIcon = (props) => (
  <svg viewBox="0 0 576 512" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      fill="currentColor"
      d="M0 432c0 26.5 21.5 48 48 48h480c26.5 0 48-21.5 48-48V256H0v176zm192-68c0-6.6 5.4-12 12-12h136c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12H204c-6.6 0-12-5.4-12-12v-40zm-128 0c0-6.6 5.4-12 12-12h72c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12H76c-6.6 0-12-5.4-12-12v-40zM576 80v48H0V80c0-26.5 21.5-48 48-48h480c26.5 0 48 21.5 48 48z"
    />
  </svg>
);

const TelegramColorIcon = (props) => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      fill="currentColor"
      d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.231-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"
    />
  </svg>
);

const WeChatIcon = (props) => (
  <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      fill="currentColor"
      d="M408.67 298.53a21 21 0 1 1 20.9-21 20.85 20.85 0 0 1-20.9 21m-102.17 0a21 21 0 1 1 20.9-21 20.84 20.84 0 0 1-20.9 21m152.09 118.86C491.1 394.08 512 359.13 512 319.51c0-71.08-68.5-129.35-154.41-129.35s-154.42 58.27-154.42 129.35 68.5 129.34 154.42 129.34c17.41 0 34.83-2.33 49.92-7 2.49-.86 3.48-1.17 4.64-1.17a16.67 16.67 0 0 1 8.13 2.34L454 462.83a11.62 11.62 0 0 0 3.48 1.17 5 5 0 0 0 4.65-4.66 14.27 14.27 0 0 0-.77-3.86c-.41-1.46-5-16-7.36-25.27a18.94 18.94 0 0 1-.33-3.47 11.4 11.4 0 0 1 5-9.35"
    />
    <path
      fill="currentColor"
      d="M246.13 178.51a24.47 24.47 0 0 1 0-48.94c12.77 0 24.38 11.65 24.38 24.47 1.16 12.82-10.45 24.47-24.38 24.47m-123.06 0A24.47 24.47 0 1 1 147.45 154a24.57 24.57 0 0 1-24.38 24.47M184.6 48C82.43 48 0 116.75 0 203c0 46.61 24.38 88.56 63.85 116.53C67.34 321.84 68 327 68 329a11.38 11.38 0 0 1-.66 4.49C63.85 345.14 59.4 364 59.21 365s-1.16 3.5-1.16 4.66a5.49 5.49 0 0 0 5.8 5.83 7.15 7.15 0 0 0 3.49-1.17L108 351c3.49-2.33 5.81-2.33 9.29-2.33a16.33 16.33 0 0 1 5.81 1.16c18.57 5.83 39.47 8.16 60.37 8.16h10.45a133.24 133.24 0 0 1-5.81-38.45c0-78.08 75.47-141 168.35-141h10.45C354.1 105.1 277.48 48 184.6 48"
    />
  </svg>
);
const AliPayIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    {...props}
  >
    <path
      fill="currentColor"
      d="M20 13.692V3.846A3.845 3.845 0 0 0 16.154 0H3.846A3.845 3.845 0 0 0 0 3.846v12.308A3.845 3.845 0 0 0 3.846 20h12.308a3.848 3.848 0 0 0 3.787-3.167c-1.02-.439-5.44-2.347-7.742-3.45c-1.755 2.122-3.589 3.396-6.356 3.396s-4.613-1.703-4.388-3.79c.145-1.368 1.084-3.605 5.161-3.22c2.148.201 3.132.604 4.886 1.182c.45-.83.83-1.745 1.114-2.72H4.847v-.77H8.69V6.077H4V5.23h4.69V3.236s.045-.315.389-.315H11V5.23h5.002v.847H11v1.384h4.078a15.726 15.726 0 0 1-1.654 4.154c1.182.43 6.575 2.077 6.575 2.077ZM5.538 15.46c-2.925 0-3.384-1.846-3.23-2.617c.154-.77 1.002-1.768 2.625-1.768c1.87 0 3.541.477 5.547 1.454c-1.407 1.837-3.144 2.93-4.942 2.93Z"
    />
  </svg>
);

const NavigateIcon = (props) => (
  <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      fill="currentColor"
      d="M502.61 233.32L278.68 9.39c-12.52-12.52-32.83-12.52-45.36 0L9.39 233.32c-12.52 12.53-12.52 32.83 0 45.36l223.93 223.93c12.52 12.53 32.83 12.53 45.36 0l223.93-223.93c12.52-12.53 12.52-32.83 0-45.36zm-100.98 12.56l-84.21 77.73c-5.12 4.73-13.43 1.1-13.43-5.88V264h-96v64c0 4.42-3.58 8-8 8h-32c-4.42 0-8-3.58-8-8v-80c0-17.67 14.33-32 32-32h112v-53.73c0-6.97 8.3-10.61 13.43-5.88l84.21 77.73c3.43 3.17 3.43 8.59 0 11.76z"
    />
  </svg>
);

const ShieldIcon = (props) => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M12 2l8 3v6c0 5-3.5 9.5-8 11-4.5-1.5-8-6-8-11V5l8-3z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DropGradientDefs = () => (
  <svg
    width="0"
    height="0"
    aria-hidden="true"
    focusable="false"
    style={{ position: "absolute", overflow: "hidden" }}
  >
    <defs>
      <linearGradient
        id={BUY_BUTTON_DROP_GRADIENT_ID}
        x1="0"
        y1="0"
        x2="0"
        y2="1"
      >
        <stop offset="0%" stopColor="var(--color-brand-400)" />
        <stop offset="55%" stopColor="var(--color-brand-600)" />
        <stop offset="100%" stopColor="var(--color-brand-700)" />
      </linearGradient>
    </defs>
  </svg>
);

const ICON_COLORS = {
  btc: "#f59e0b",
  usdt: "#26A17B",
  eth: "#627EEA",
  card: "currentColor",
  telegram: "#229ED9",
  wechat: "#09B83E",
  alipay: "#1478ff",
};

const PAYMENT_METHODS = [
  {
    id: "TELEGRAM",
    label: "Card · Telegram",
    // badge: { text: "+20%", variant: "discount" },
    enabled: true,
    renderIcon: () => (
      <>
        <CreditCardIcon style={{ color: ICON_COLORS.card }} />
        <TelegramColorIcon
          style={{ color: ICON_COLORS.telegram, width: 20, height: 20 }}
        />
      </>
    ),
  },

  // {
  //   id: "CREDIT_CARD2",
  //   label: "Card",
  //   // badge: { text: "+10%", variant: "fee" },
  //   enabled: true,
  //   renderIcon: () => <CreditCardIcon style={{ color: ICON_COLORS.card }} />,
  // },

  {
    id: "CRYPTO",
    label: "Crypto",
    enabled: true,
    renderIcon: () => (
      <>
        <BTCIcon
          style={{ color: ICON_COLORS.btc, marginRight: -5, marginTop: 2 }}
        />
        <USDTIcon style={{ color: ICON_COLORS.usdt, width: 30, height: 30 }} />
        <ETHIcon style={{ color: ICON_COLORS.eth, marginLeft: -5 }} />
      </>
    ),
  },

  {
    id: "ALIPAY",
    label: "AliPay",
    enabled: true,
    asianOnly: true,
    renderIcon: () => <AliPayIcon style={{ color: ICON_COLORS.alipay }} />,
  },
  {
    id: "WECHATPAY",
    label: "WeChat",
    enabled: true,
    asianOnly: true,
    renderIcon: () => <WeChatIcon style={{ color: ICON_COLORS.wechat }} />,
  },
];

const DEFAULT_METHOD = PAYMENT_METHODS.find((m) => m.enabled)?.id ?? null;

const PACKAGES = [
  {
    id: "mega",
    planId: "6a06cb86ec3f6196f0da00f3",
    name: "Mega",
    badge: "BEST VALUE",
    badgeVariant: "soft",
    amount: 300,
    drops: 11000,
    bonusDrops: 2000,
    features: [
      { label: "HD mode", tier: "premium" },
      { label: "Pro-Grade Exports: No watermarks", tier: "premium" },
      { label: "Ultra-HD Rendering", tier: "premium" },
      { label: "Unlimited Generation History", tier: "premium" },
      { label: "Faster Generation", tier: "check" },
      { label: "Full-capability video generation", tier: "check" },
      { label: "Instant Priority Access", tier: "check" },
    ],
  },
  {
    id: "pro",
    planId: "6a06cb86ec3f6196f0da00f2",
    name: "Pro",
    badge: "MOST POPULAR",
    badgeVariant: "solid",
    popular: true,
    amount: 120,
    drops: 4300,
    bonusDrops: 600,
    features: [
      { label: "HD mode", tier: "premium" },
      { label: "Pro-Grade Exports: No watermarks", tier: "premium" },
      { label: "Ultra-HD Rendering", tier: "premium" },
      { label: "Unlimited Generation History", tier: "missing" },
      { label: "48h Generation History", tier: "check" },
      { label: "Faster Generation", tier: "check" },
      { label: "Full-capability video generation", tier: "check" },
      { label: "Instant Priority Access", tier: "check" },
    ],
  },
  {
    id: "plus",
    planId: "6a06cb86ec3f6196f0da00f1",
    name: "Plus",
    amount: 60,
    drops: 2100,
    bonusDrops: 300,
    features: [
      { label: "HD mode", tier: "premium" },
      { label: "Pro-Grade Exports: No watermarks", tier: "premium" },
      { label: "Ultra-HD Rendering", tier: "premium" },
      { label: "Unlimited Generation History", tier: "missing" },
      { label: "48h Generation History", tier: "check" },
      { label: "Faster Generation", tier: "check" },
      { label: "Full-capability video generation", tier: "check" },
      { label: "Instant Priority Access", tier: "check" },
    ],
  },
  {
    id: "basic",
    planId: "6a06cb86ec3f6196f0da00f0",
    name: "Basic",
    amount: 40,
    drops: 1300,
    bonusDrops: 100,
    features: [
      { label: "HD mode", tier: "missing" },
      { label: "Pro-Grade Exports: No watermarks", tier: "premium" },
      { label: "Ultra-HD Rendering", tier: "premium" },
      { label: "Unlimited Generation History", tier: "missing" },
      { label: "48h Generation History", tier: "check" },
      { label: "Faster Generation", tier: "check" },
      { label: "Full-capability video generation", tier: "check" },
      { label: "Instant Priority Access", tier: "check" },
    ],
  },
  {
    id: "starter",
    planId: "6a06cb85ec3f6196f0da00ef",
    name: "Starter",
    badge: "STARTER",
    badgeVariant: "muted",
    amount: 20,
    drops: 600,
    bonusDrops: 0,
    features: [
      { label: "HD mode", tier: "missing" },
      { label: "Pro-Grade Exports: No watermarks", tier: "premium" },
      { label: "Ultra-HD Rendering", tier: "premium" },
      { label: "Unlimited Generation History", tier: "missing" },
      { label: "48h Generation History", tier: "check" },
      { label: "Faster Generation", tier: "check" },
      { label: "Full-capability video generation", tier: "check" },
      { label: "Instant Priority Access", tier: "check" },
    ],
  },
];

const DROPS_PER_VIDEO = 30;
const DEFAULT_INDEX = PACKAGES.findIndex((p) => p.popular);

const TopUpModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loggedIn = useSelector(selectLoggedIn);
  const user = useSelector(selectUser);
  const asianPaymentsEnabled = showAsianPayments(user);
  const paymentMethods = PAYMENT_METHODS.filter(
    (method) => !method.asianOnly || asianPaymentsEnabled,
  );
  const isMobile = useMediaQuery({ maxWidth: 700 });
  const isOnSubscriptionPage = location.pathname === "/subscription";

  const [activeIndex, setActiveIndex] = useState(
    DEFAULT_INDEX >= 0 ? DEFAULT_INDEX : 1,
  );
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(DEFAULT_METHOD);
  const [pendingMethod, setPendingMethod] = useState(null);
  const [shouldLockScroll, setShouldLockScroll] = useState(isOpen);
  const [isMobileModalMounted, setIsMobileModalMounted] = useState(isOpen);
  const [isMobileModalVisible, setIsMobileModalVisible] = useState(false);
  const [mobileDragOffset, setMobileDragOffset] = useState(0);
  const [isDraggingSheet, setIsDraggingSheet] = useState(false);

  const viewportRef = useRef(null);
  const slideRefs = useRef([]);
  const sheetDragRef = useRef({
    active: false,
    startY: 0,
    lastY: 0,
    lastTime: 0,
    velocity: 0,
  });
  const setSlideRef = useCallback(
    (idx) => (el) => {
      slideRefs.current[idx] = el;
    },
    [],
  );

  const resetModalState = useCallback(() => {
    setActiveIndex(DEFAULT_INDEX >= 0 ? DEFAULT_INDEX : 1);
    setSelectedPlan(null);
    setSelectedMethod(DEFAULT_METHOD);
    setPendingMethod(null);
    setMobileDragOffset(0);
    setIsDraggingSheet(false);
  }, []);

  useEffect(() => {
    if (isOpen) setShouldLockScroll(true);
  }, [isOpen]);

  // Reset the "Redirecting…" state when the user returns via the
  // browser's back-forward cache after bailing on the gateway.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) setPendingMethod(null);
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  useLockBodyScroll(shouldLockScroll);

  const handleExitComplete = useCallback(() => {
    resetModalState();
    setShouldLockScroll(false);
  }, [resetModalState]);

  const triggerClose = useCallback(() => onClose?.(), [onClose]);

  useEffect(() => {
    if (!isMobile) return undefined;

    let firstFrame = null;
    let secondFrame = null;
    let exitTimer = null;

    if (isOpen) {
      setIsMobileModalMounted(true);
      setIsMobileModalVisible(false);
      setMobileDragOffset(0);
      setIsDraggingSheet(false);

      firstFrame = requestAnimationFrame(() => {
        secondFrame = requestAnimationFrame(() => {
          setIsMobileModalVisible(true);
        });
      });
    } else if (isMobileModalMounted) {
      setIsMobileModalVisible(false);
      setIsDraggingSheet(false);
      exitTimer = setTimeout(() => {
        setIsMobileModalMounted(false);
        handleExitComplete();
      }, MOBILE_SHEET_TRANSITION_MS);
    }

    return () => {
      if (firstFrame) cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
      if (exitTimer) clearTimeout(exitTimer);
    };
  }, [handleExitComplete, isMobile, isMobileModalMounted, isOpen]);

  const handleSheetDragStart = useCallback(
    (event) => {
      if (!isMobile || !isMobileModalVisible) return;

      const now = performance.now();
      sheetDragRef.current = {
        active: true,
        startY: event.clientY,
        lastY: event.clientY,
        lastTime: now,
        velocity: 0,
      };
      setIsDraggingSheet(true);
      setMobileDragOffset(0);
      event.currentTarget.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    },
    [isMobile, isMobileModalVisible],
  );

  const handleSheetDragMove = useCallback((event) => {
    const drag = sheetDragRef.current;
    if (!drag.active) return;

    const now = performance.now();
    const elapsed = Math.max(now - drag.lastTime, 1);
    const rawOffset = Math.max(0, event.clientY - drag.startY);
    const dampedOffset =
      rawOffset > 180 ? 180 + (rawOffset - 180) * 0.35 : rawOffset;

    drag.velocity = (event.clientY - drag.lastY) / elapsed;
    drag.lastY = event.clientY;
    drag.lastTime = now;
    setMobileDragOffset(dampedOffset);
    event.preventDefault();
  }, []);

  const finishSheetDrag = useCallback(
    (event) => {
      const drag = sheetDragRef.current;
      if (!drag.active) return;

      const rawOffset = Math.max(0, event.clientY - drag.startY);
      const shouldClose =
        rawOffset > SWIPE_CLOSE_DISTANCE ||
        drag.velocity > SWIPE_CLOSE_VELOCITY;

      drag.active = false;
      setIsDraggingSheet(false);
      event.currentTarget.releasePointerCapture?.(event.pointerId);

      if (shouldClose) {
        triggerClose();
        return;
      }

      setMobileDragOffset(0);
    },
    [triggerClose],
  );

  const cancelSheetDrag = useCallback((event) => {
    const drag = sheetDragRef.current;
    if (!drag.active) return;

    drag.active = false;
    setIsDraggingSheet(false);
    setMobileDragOffset(0);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  // Programmatic scroll to a slide. The instant path must temporarily
  // disable BOTH `scroll-snap-type` and `scroll-behavior: smooth` —
  // otherwise the browser either re-snaps to idx 0 or smooth-animates
  // the jump (which would replay as a "swipe" each time we open the
  // modal on the popular slide).
  const scrollToIndex = useCallback((idx, smooth = true) => {
    const viewport = viewportRef.current;
    const slide = slideRefs.current[idx];
    if (!viewport || !slide) return;
    const targetLeft =
      slide.offsetLeft - (viewport.clientWidth - slide.offsetWidth) / 2;
    if (smooth) {
      viewport.scrollTo({ left: targetLeft, behavior: "smooth" });
      return;
    }
    const prevSnap = viewport.style.scrollSnapType;
    const prevBehavior = viewport.style.scrollBehavior;
    viewport.style.scrollSnapType = "none";
    viewport.style.scrollBehavior = "auto";
    viewport.scrollLeft = targetLeft;
    requestAnimationFrame(() => {
      viewport.style.scrollSnapType = prevSnap;
      viewport.style.scrollBehavior = prevBehavior;
    });
  }, []);

  // Snap to the popular slide on open / when returning from the picker.
  useEffect(() => {
    if (!isOpen || selectedPlan || (isMobile && !isMobileModalMounted)) return;
    scrollToIndex(DEFAULT_INDEX >= 0 ? DEFAULT_INDEX : 1, false);
  }, [isMobile, isMobileModalMounted, isOpen, selectedPlan, scrollToIndex]);

  // Scroll-driven carousel state. Desktop keeps the scale/opacity treatment;
  // mobile only updates the active dot so the bottom-sheet entrance is not
  // competing with per-slide style writes.
  useEffect(() => {
    if (!isOpen || selectedPlan || (isMobile && !isMobileModalMounted)) return;
    const viewport = viewportRef.current;
    if (!viewport) return;

    const SCALE_FLOOR = 0.92;
    const OPACITY_FLOOR = isMobile ? 0.85 : 0.5;
    let metrics = [];

    const measure = () => {
      metrics = slideRefs.current.map((slide) =>
        slide
          ? { el: slide, center: slide.offsetLeft + slide.offsetWidth / 2 }
          : null,
      );
    };

    let raf = null;
    let lastBest = -1;
    const update = () => {
      const center = viewport.scrollLeft + viewport.clientWidth / 2;
      const falloff = viewport.clientWidth / 2;
      let bestIdx = 0;
      let bestDist = Infinity;
      for (let i = 0; i < metrics.length; i++) {
        const m = metrics[i];
        if (!m) continue;
        const dist = Math.abs(m.center - center);
        const t = dist >= falloff ? 1 : dist / falloff;
        if (!isMobile) {
          const opacity = 1 - t * (1 - OPACITY_FLOOR);
          const scale = 1 - t * (1 - SCALE_FLOOR);
          m.el.style.transform = `translate3d(0,0,0) scale(${scale.toFixed(3)})`;
          m.el.style.opacity = opacity.toFixed(3);
        }
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      }
      if (bestIdx !== lastBest) {
        lastBest = bestIdx;
        setActiveIndex(bestIdx);
      }
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        update();
      });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        measure();
        update();
      });
    });

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            measure();
            update();
          })
        : null;
    ro?.observe(viewport);

    viewport.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      viewport.removeEventListener("scroll", onScroll);
      ro?.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isMobile, isMobileModalMounted, isOpen, selectedPlan]);

  const totalSlides = PACKAGES.length;
  const goPrev = () => scrollToIndex(Math.max(0, activeIndex - 1));
  const goNext = () =>
    scrollToIndex(Math.min(totalSlides - 1, activeIndex + 1));

  const handleBuy = (pkg) => {
    if (!loggedIn) {
      toast.error("Please sign in first.");
      return;
    }
    setSelectedPlan(pkg);
    setSelectedMethod(DEFAULT_METHOD);
    setPendingMethod(null);
  };

  const handlePayNow = () => {
    if (!selectedPlan || !selectedMethod || pendingMethod) return;
    setPendingMethod(selectedMethod);
    dispatch(
      choosePlan({
        paymentMethod: selectedMethod,
        planId: selectedPlan.planId,
      }),
    )
      .unwrap()
      .then((res) => {
        if (res?.data?.link) {
          window.location.href = res.data.link;
          return;
        }
        // No gateway link returned (e.g. flow that credits the account
        // directly) — close the modal and drop the user on the subscription
        // page so they can see the updated balance.
        setPendingMethod(null);
        triggerClose();
        if (!isOnSubscriptionPage) navigate("/subscription");
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to start checkout.");
        setPendingMethod(null);
      });
  };

  const handleBackToPlans = () => {
    setSelectedPlan(null);
    setPendingMethod(null);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) triggerClose();
  };

  const desktopCardMotionProps = {
    initial: { opacity: 0, y: 16, scale: 0.96 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 16, scale: 0.96 },
    transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
  };

  const desktopBackdropMotionProps = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.2, ease: "easeOut" },
  };

  const shouldRenderModal = isMobile ? isMobileModalMounted : isOpen;
  const activeBackdropProps = isMobile
    ? { $useCssMotion: true, $mobileOpen: isMobileModalVisible }
    : desktopBackdropMotionProps;
  const activeCardProps = isMobile
    ? {
        $useCssMotion: true,
        $mobileOpen: isMobileModalVisible,
        $dragOffset: mobileDragOffset,
        $isDragging: isDraggingSheet,
      }
    : desktopCardMotionProps;

  return createPortal(
    <AnimatePresence onExitComplete={isMobile ? undefined : handleExitComplete}>
      {shouldRenderModal && (
        <Backdrop {...activeBackdropProps} onClick={handleBackdropClick}>
          <Card {...activeCardProps}>
            <DropGradientDefs />
            {isMobile && (
              <DragHandle
                aria-label="Swipe down to close"
                role="button"
                tabIndex={0}
                onPointerDown={handleSheetDragStart}
                onPointerMove={handleSheetDragMove}
                onPointerUp={finishSheetDrag}
                onPointerCancel={cancelSheetDrag}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    triggerClose();
                  }
                }}
              />
            )}
            <Header $showOnMobile={!!selectedPlan}>
              {selectedPlan ? (
                <BackButton
                  type="button"
                  onClick={handleBackToPlans}
                  aria-label="Back to plans"
                >
                  <ChevronSvg />
                  Back to plans
                </BackButton>
              ) : (
                <Title>Premium plans</Title>
              )}
              <CloseButton
                onClick={triggerClose}
                aria-label="Close"
                type="button"
              >
                <CloseSvg />
              </CloseButton>
            </Header>

            <Body>
              {selectedPlan ? (
                <>
                  {/* Step 2: payment-method picker */}
                  <PlanSummary>
                    <PlanSummaryHeader as="div">
                      <PlanSummaryIcon aria-hidden="true">
                        <DropIcon size={28} />
                      </PlanSummaryIcon>
                      <PlanSummaryText>
                        <PlanSummaryDrops>
                          {priceFormatter(selectedPlan.drops)} Drops
                        </PlanSummaryDrops>
                        <PlanSummaryName>
                          {selectedPlan.name} · $
                          {(selectedPlan.amount / selectedPlan.drops).toFixed(
                            selectedPlan.amount / selectedPlan.drops < 0.1
                              ? 3
                              : 2,
                          )}
                          /Drop
                        </PlanSummaryName>
                      </PlanSummaryText>
                      <PlanSummaryPrice>
                        ${selectedPlan.amount.toFixed(2)}
                      </PlanSummaryPrice>
                    </PlanSummaryHeader>
                  </PlanSummary>

                  <PaymentBox>
                    <PaymentGrid role="radiogroup" aria-label="Payment methods">
                      {paymentMethods.map((method) => {
                        const checked =
                          method.enabled && selectedMethod === method.id;
                        return (
                          <PaymentMethodCard
                            key={method.id}
                            type="button"
                            role="radio"
                            aria-checked={checked}
                            $selected={checked}
                            $disabled={!method.enabled}
                            disabled={!method.enabled}
                            onClick={() =>
                              method.enabled && setSelectedMethod(method.id)
                            }
                          >
                            {method.badge && (
                              <MethodBadge $variant={method.badge.variant}>
                                {method.badge.text}
                              </MethodBadge>
                            )}
                            {!method.enabled && <LockBadge>Soon</LockBadge>}
                            <MethodIcons aria-hidden="true">
                              {method.renderIcon()}
                            </MethodIcons>
                            <MethodLabel>{method.label}</MethodLabel>
                          </PaymentMethodCard>
                        );
                      })}
                    </PaymentGrid>

                    {selectedMethod === "TELEGRAM" ? (
                      <TelegramGuide>
                        <TelegramGuideHeader>
                          <NavigateIcon />
                          <span>
                            You'll be redirected to our Telegram bot to securely
                            complete your payment.
                          </span>
                        </TelegramGuideHeader>
                        <TelegramGuidePreviews aria-hidden="true">
                          <TelegramGuideThumb $position="center bottom">
                            <img src={telegramStars1} alt="" loading="lazy" />
                          </TelegramGuideThumb>
                          <TelegramGuideThumb $position="center top">
                            <img src={telegramStars2} alt="" loading="lazy" />
                          </TelegramGuideThumb>
                          <TelegramGuideThumb $position="center top">
                            <img src={telegramStars3} alt="" loading="lazy" />
                          </TelegramGuideThumb>
                        </TelegramGuidePreviews>
                        <TelegramGuideFooter>
                          For complete instructions, please refer to{" "}
                          <Link
                            to="/telegram-payment-guide"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            this guide
                          </Link>
                          .
                        </TelegramGuideFooter>
                      </TelegramGuide>
                    ) : (
                      <SecureNote>
                        <NavigateIcon />
                        <span>
                          You'll be redirected to a secure page to finish your
                          transaction after clicking submit.
                        </span>
                      </SecureNote>
                    )}

                    <PayNowButton
                      type="button"
                      onClick={handlePayNow}
                      disabled={!selectedMethod || !!pendingMethod}
                    >
                      {pendingMethod ? "Redirecting…" : "Pay now"}
                    </PayNowButton>
                  </PaymentBox>

                  <Footnote>
                    <ShieldIcon
                      width={14}
                      height={14}
                      style={{ marginRight: 6, verticalAlign: "-2px" }}
                    />
                    Payments are secure and encrypted
                  </Footnote>
                </>
              ) : (
                <>
                  {/* Step 1: plan carousel */}
                  <CarouselWrapper>
                    {!isMobile && (
                      <ArrowButton
                        type="button"
                        $side="left"
                        $disabled={activeIndex === 0}
                        onClick={goPrev}
                        aria-label="Previous plan"
                      >
                        <ChevronSvg />
                        <ArrowButtonLabel>Prev</ArrowButtonLabel>
                      </ArrowButton>
                    )}

                    <CarouselViewport ref={viewportRef}>
                      <CarouselTrack>
                        <CarouselSpacer aria-hidden="true" />
                        {PACKAGES.map((pkg, idx) => {
                          const perDrop = pkg.amount / pkg.drops;
                          const perDropLabel =
                            pkg.id === "mega"
                              ? `$${perDrop.toFixed(perDrop < 0.1 ? 3 : 2) - 0.001} per Drop`
                              : `$${perDrop.toFixed(perDrop < 0.1 ? 3 : 2)} per Drop`;
                          return (
                            <CarouselSlide
                              key={pkg.id}
                              ref={setSlideRef(idx)}
                              $active={idx === activeIndex}
                            >
                              <PlanCard $popular={pkg.popular}>
                                <PlanCardGlow
                                  $popular={pkg.popular}
                                  aria-hidden="true"
                                />
                                {pkg.badge && (
                                  <PopularBadge $variant={pkg.badgeVariant}>
                                    {pkg.badge}
                                  </PopularBadge>
                                )}

                                <CardHero>
                                  <CardHeroText>
                                    <CardHeroValue>
                                      <CardHeroValueNumber>
                                        {priceFormatter(pkg.drops)}
                                      </CardHeroValueNumber>
                                      <CardHeroValueLabel>
                                        Drops
                                      </CardHeroValueLabel>
                                    </CardHeroValue>
                                    <CardHeroSubtitle>
                                      {perDropLabel}
                                    </CardHeroSubtitle>
                                    {pkg.bonusDrops > 0 && (
                                      <>
                                        <CardHeroBonus>
                                          <span
                                            style={{
                                              marginTop: -2.5,
                                              marginRight: -3,
                                            }}
                                          >
                                            +
                                          </span>
                                          {priceFormatter(pkg.bonusDrops)} bonus
                                          drops included
                                        </CardHeroBonus>
                                      </>
                                    )}
                                  </CardHeroText>
                                  <CardHeroIcon aria-hidden="true">
                                    <DropIcon
                                      size={40}
                                      color={`url(#${BUY_BUTTON_DROP_GRADIENT_ID})`}
                                    />
                                  </CardHeroIcon>
                                </CardHero>

                                <FeaturesList>
                                  {pkg.features.map((f) => (
                                    <Feature key={f.label} $tier={f.tier}>
                                      <FeatureIcon
                                        $tier={f.tier}
                                        aria-hidden="true"
                                      >
                                        {f.tier === "premium"
                                          ? "★"
                                          : f.tier === "missing"
                                            ? "✕"
                                            : "✓"}
                                      </FeatureIcon>
                                      <FeatureLabel $tier={f.tier}>
                                        {f.label}
                                      </FeatureLabel>
                                    </Feature>
                                  ))}
                                </FeaturesList>

                                <CardFooterInfo>
                                  <CardFooterLine>
                                    You get{" "}
                                    {priceFormatter(
                                      Math.floor(pkg.drops / DROPS_PER_VIDEO),
                                    )}{" "}
                                    video generations
                                  </CardFooterLine>
                                  {/* <CardFooterLine $accent>
                                    ✨ Drops never expire
                                  </CardFooterLine> */}
                                </CardFooterInfo>

                                <ActionArea>
                                  <BuyButton
                                    type="button"
                                    onClick={() => handleBuy(pkg)}
                                  >
                                    Buy
                                    <BuyAmount>
                                      · ${pkg.amount.toFixed(2)}
                                    </BuyAmount>
                                  </BuyButton>
                                </ActionArea>
                              </PlanCard>
                            </CarouselSlide>
                          );
                        })}
                        <CarouselSpacer aria-hidden="true" />
                      </CarouselTrack>
                    </CarouselViewport>

                    {!isMobile && (
                      <ArrowButton
                        type="button"
                        $side="right"
                        $disabled={activeIndex === totalSlides - 1}
                        onClick={goNext}
                        aria-label="Next plan"
                      >
                        <ArrowButtonLabel>Next</ArrowButtonLabel>
                        <ChevronSvg style={{ transform: "rotate(180deg)" }} />
                      </ArrowButton>
                    )}
                  </CarouselWrapper>

                  <DotsRow>
                    {PACKAGES.map((pkg, idx) => (
                      <Dot
                        key={pkg.id}
                        type="button"
                        $active={idx === activeIndex}
                        onClick={() => scrollToIndex(idx)}
                        aria-label={`Go to ${pkg.name}`}
                      />
                    ))}
                  </DotsRow>

                  <Footnote>Payments are secure and encrypted</Footnote>

                  {!isOnSubscriptionPage && (
                    <CryptoLink
                      type="button"
                      onClick={() => {
                        triggerClose();
                        navigate("/subscription");
                      }}
                    >
                      Go to subscription page →
                    </CryptoLink>
                  )}
                </>
              )}
            </Body>
          </Card>
        </Backdrop>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default TopUpModal;
